import type { Brands } from '@edreamsodigeo/frontend-core';

type ColorKey = 'primary' | 'secondary';

type PrismaColor = `${ColorKey}.${number}`;

interface ColorSet {
  default: PrismaColor;
  prime: PrismaColor;
}

interface GradientType {
  default: string;
  prime: string;
  horizontal: string;
  lightToDark: string;
}

interface ColorMap {
  background: ColorSet;
  gradientBackground: GradientType;
  text: ColorSet;
  auxiliaryText: ColorSet;
}

export const KEYS_TO_COLORS = {
  PRIMARY_30: 'primary.30',
  PRIMARY_50: 'primary.50',
  PRIMARY_70: 'primary.70',
  PRIMARY_90: 'primary.90',
  SECONDARY_50: 'secondary.50',
  SECONDARY_30: 'secondary.30',
  SECONDARY_70: 'secondary.70',
} as const;

const DEFAULT_COLOR_MAP: ColorMap = {
  background: {
    default: KEYS_TO_COLORS.PRIMARY_50,
    prime: KEYS_TO_COLORS.PRIMARY_90,
  },
  gradientBackground: {
    default: 'linear-gradient(90.11deg, #003A6B 0.09%, #002647 99.9%)',
    prime: 'linear-gradient(90.11deg, #003A6B 0.09%, #002647 99.9%)',
    horizontal: 'linear-gradient(180deg, #002C52 0%, #000D29 100%)',
    lightToDark: 'linear-gradient(90.11deg, #003A6B 0.09%, #002647 99.9%)',
  },
  text: {
    default: KEYS_TO_COLORS.SECONDARY_50,
    prime: KEYS_TO_COLORS.SECONDARY_50,
  },
  auxiliaryText: {
    default: KEYS_TO_COLORS.SECONDARY_70,
    prime: KEYS_TO_COLORS.SECONDARY_70,
  },
} as const;

const CUSTOM_COLOR_MAP: Partial<Record<Brands, ColorMap>> = {
  OP: {
    background: {
      default: KEYS_TO_COLORS.PRIMARY_90,
      prime: KEYS_TO_COLORS.PRIMARY_90,
    },
    gradientBackground: {
      default: 'linear-gradient(180deg, #262626 0%, #000000 100%)',
      prime: 'linear-gradient(180deg, #262626 0%, #000000 100%)',
      horizontal: 'linear-gradient(180deg, #262626 0%, #000000 100%)',
      lightToDark: 'linear-gradient(90deg, #262626 0%, #000000 100%)',
    },
    text: {
      default: KEYS_TO_COLORS.PRIMARY_50,
      prime: KEYS_TO_COLORS.PRIMARY_50,
    },
    auxiliaryText: {
      default: KEYS_TO_COLORS.PRIMARY_70,
      prime: KEYS_TO_COLORS.PRIMARY_70,
    },
  },
  GO: {
    ...DEFAULT_COLOR_MAP,
    gradientBackground: {
      default:
        'linear-gradient(316.46deg, #123300 2.11%, #123200 17.6%, #0B1F00 88.83%)',
      prime:
        'linear-gradient(316.46deg, #123300 2.11%, #123200 17.6%, #0B1F00 88.83%)',
      horizontal: 'linear-gradient(180deg, #123300 0%, #0B1F00 100%)',
      lightToDark: 'linear-gradient(90deg, #123300 0%, #0B1F00 100%)',
    },
    text: {
      default: KEYS_TO_COLORS.PRIMARY_30,
      prime: KEYS_TO_COLORS.PRIMARY_30,
    },
    auxiliaryText: {
      default: KEYS_TO_COLORS.PRIMARY_30,
      prime: KEYS_TO_COLORS.PRIMARY_30,
    },
  },
} as const;

interface BrandColorArgs {
  brand?: Brands;
  isPrime?: boolean;
  gradientType?: keyof GradientType;
}

interface BrandColor {
  backgroundColor: PrismaColor;
  gradientBackgroundColor: string;
  textColor: PrismaColor;
  auxiliaryTextColor: PrismaColor;
}

export const useBrandColor = ({
  brand,
  isPrime = false,
  gradientType = 'default',
}: BrandColorArgs): BrandColor => {
  const type = isPrime ? 'prime' : 'default';
  return {
    backgroundColor:
      (brand && CUSTOM_COLOR_MAP[brand]?.background[type]) ||
      DEFAULT_COLOR_MAP.background[type],
    gradientBackgroundColor:
      (brand && CUSTOM_COLOR_MAP[brand]?.gradientBackground[gradientType]) ||
      DEFAULT_COLOR_MAP.gradientBackground[type],
    textColor:
      (brand && CUSTOM_COLOR_MAP[brand]?.text[type]) ||
      DEFAULT_COLOR_MAP.text[type],
    auxiliaryTextColor:
      (brand && CUSTOM_COLOR_MAP[brand]?.auxiliaryText[type]) ||
      DEFAULT_COLOR_MAP.auxiliaryText[type],
  };
};
